import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Linkedin, ArrowUpRight } from 'lucide-react';
import { profileData } from '../data/mock';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Each nav link gets its own accent dot — turns the menu into a tiny multicolour rainbow.
const NAV_LINKS = [
  { href: '#about',       label: 'About',            isAnchor: true,  accent: '#6b8e7f' },
  { href: '#experience',  label: 'Experience',       isAnchor: true,  accent: '#5b8fa8' },
  { href: '#skills',      label: 'Skills',           isAnchor: true,  accent: '#c77b5a' },
  { href: '#education',   label: 'Education',        isAnchor: true,  accent: '#8c6e89' },
  { href: '/blog',        label: 'Blog',             isAnchor: false, accent: '#8c6e89' },
  { href: '/memories',    label: 'Wall of Fame',     isAnchor: false, accent: '#c77b5a' },
  { href: '/apps',        label: 'Apps',             isAnchor: false, accent: '#5b8fa8' },
  { href: '/booking',     label: 'Book Appointment', isAnchor: false, accent: '#c9a84c' },
  { href: '#contact',     label: 'Contact',          isAnchor: true,  accent: '#6b8e7f' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateAnchor = (href) => {
    if (!isHomePage) {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#faf6ee]/85 backdrop-blur-2xl border-b border-[#e6dfd0]/70 shadow-[0_1px_0_rgba(21,40,70,0.04)]'
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="header-logo-link"
          >
            <img
              src="/sn-logo.png"
              alt="Dr. Srijit Nair"
              className="h-12 sm:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex w-10 h-10 items-center justify-center rounded-full bg-white/60 backdrop-blur-xl border border-[#e6dfd0] text-[#152846] hover:bg-white hover:border-[#152846]/30 hover:text-[#152846] transition-all duration-300"
              aria-label="LinkedIn"
              data-testid="header-linkedin-link"
            >
              <Linkedin size={16} strokeWidth={2.25} />
            </a>

            <a
              href={`mailto:${profileData.email}`}
              className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#152846] text-[#faf6ee] text-sm font-semibold hover:bg-[#0b182e] transition-all duration-300 hover:shadow-[0_10px_30px_-12px_rgba(21,40,70,0.5)] hover:-translate-y-0.5"
              data-testid="header-cta-btn"
            >
              Get in Touch
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-white/60 backdrop-blur-xl border border-[#e6dfd0] text-[#152846] hover:bg-white hover:border-[#152846]/30 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#152846]/30"
                  aria-label="Open navigation menu"
                  data-testid="nav-menu-trigger"
                >
                  <Menu size={16} strokeWidth={2.5} className="sm:hidden" />
                  <span className="hidden sm:inline text-sm font-medium tracking-wide">Menu</span>
                  <ChevronDown size={14} strokeWidth={2.5} className="hidden sm:inline opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={12}
                className="w-[280px] p-2 bg-[#fffdf8]/95 backdrop-blur-3xl border border-[#e6dfd0] rounded-2xl shadow-[0_20px_60px_-20px_rgba(21,40,70,0.25)]"
                data-testid="nav-menu-panel"
              >
                {NAV_LINKS.map((link) => {
                  const isActive = !link.isAnchor && location.pathname.startsWith(link.href);
                  const testid = `nav-${link.label.toLowerCase().replace(/\s/g, '-')}`;
                  const baseCls =
                    'px-4 py-3 text-base rounded-xl transition-all duration-200 cursor-pointer flex items-center gap-3 font-sans focus:bg-[#faf6ee] focus:text-[#152846] outline-none';
                  return link.isAnchor ? (
                    <DropdownMenuItem
                      key={link.href}
                      onSelect={() => navigateAnchor(link.href)}
                      className={`${baseCls} text-[#152846] hover:bg-[#faf6ee] hover:pl-6`}
                      data-testid={testid}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: link.accent }} />
                      {link.label}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      key={link.href}
                      asChild
                      className={`${baseCls} ${isActive ? 'bg-[#faf6ee] font-semibold' : 'text-[#152846] hover:bg-[#faf6ee] hover:pl-6'}`}
                    >
                      <Link to={link.href} data-testid={testid}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: link.accent, boxShadow: isActive ? `0 0 0 3px ${link.accent}22` : 'none' }} />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}

                <DropdownMenuSeparator className="my-2 bg-[#e6dfd0]" />

                <a
                  href={`mailto:${profileData.email}`}
                  className="sm:hidden flex items-center justify-center gap-1.5 mx-1 mb-1 px-4 py-2.5 rounded-xl bg-[#152846] text-[#faf6ee] text-sm font-semibold hover:bg-[#0b182e] transition-colors"
                  data-testid="nav-menu-cta"
                >
                  Get in Touch
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
